import { Component, Input, OnInit } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";

import { CustomEventService } from '@shared/services/custom-event.service';

import ActionTypesEnum from '@shared/enums/action-constant.enum';

import {
  createFormControl,
  createFormGroup,
  requestObjectMapper,
  validateFormGroup
} from "@shared/utils/form.util";
import { UntilDestroy,untilDestroyed } from '@ngneat/until-destroy';
import { GitwiserService } from "@shared/services/gitwiser.service";
import { AgileSpaceService } from "@shared/services/agile-space.service";

@UntilDestroy()
@Component({
  selector: 'app-add-analysis',
  templateUrl: './add-analysis-modal.component.html'
})
export class AddAnalysisModalComponent implements OnInit {

  @Input() fields: Widget.Field[];
  @Input() teams: Team.TeamModel[];
  @Input() loading: boolean;
  @Input() isExclusion?: boolean;
  @Input() addonLabel?: string;


  addAnalysisForm: FormGroup;

  exclusion = [];
  scoreCards = [];
  deploymentAnalyticsPatterns = [];
  analysisDateRange = '';

  selectedFields: Map<string, any> = new Map();
  exclusionsList: { label: string; value: string }[] = [];
  hotfixPatternList: { label: string; value: string }[] = [];
  dateRangeList: { label: string; value: string }[] = [];
  deploymentAnalyticsFields: Widget.Field[] = [];
  deploymentAnalyticsApmFields: Widget.Field[] = [];
  resetInputs = false;
  agilespaceBoard: Base.KeyLabelModel[] = [];
  selectedAgileBoard = [];

  constructor(private customEventService: CustomEventService,
    private gitwiserService: GitwiserService,
    private agileSpaceService: AgileSpaceService) { }

  // sets selectedFields Map data with each field that comes with pre selections
  setSelectedFieldData = (fields: any[] = []): void => {

    fields.forEach(field => {
      let value = null;

      const selections = field.data.selections;

      if (selections) {
        value = selections instanceof Array ?
          selections.map(item => item.key) :
          selections instanceof Object ? selections.key : selections;
      }

      this.selectedFields.set(field.key, value);
    });

  };

  ngOnInit(): void {

    this.loadAgileBoards()
    this.analysisDateRange = 'FROM_LAST_24_MONTHS';
    this.dateRangeList.push(
      { label: 'Last 6 Months', value: 'FROM_LAST_6_MONTHS' },
      { label: 'Last 12 Months', value: 'FROM_LAST_12_MONTHS' },
      { label: 'Last 24 Months', value: 'FROM_LAST_24_MONTHS' },
      { label: 'Last 36 Months', value: 'FROM_LAST_36_MONTHS' },
      { label: 'From Beginning', value: 'FROM_BEGINNING' });

    this.exclusionsList.push(
      { label: '**/*.xml', value: '**/*.xml' },
      { label: '**/*.json', value: '**/*.json' },
      { label: '**/*.xsd', value: '**/*.xsd' },
      { label: '**/*gradle*', value: '**/*gradle*' });

    this.hotfixPatternList.push(
      { label: 'hotfix', value: 'hotfix' },
      { label: 'incident', value: 'incident' },
      { label: 'error', value: 'error' });

    if(this.fields) {
      this.fields.forEach(field => {
        field.required = field.validators?.find(item => item === 'required');

        const regex = field.validators?.find(item => item.startsWith('regex::'));

        if (regex) {
          field.regex = regex.substring(7, regex.length);
        }

        if (field.data?.content?.length === 1) {
          const selection = {key: field.data.content[0].key};
          field.data.selections = field.multiSelect ? [selection] : selection;
        }
      });

      this.addAnalysisForm = createFormGroup(this.fields);
      this.setSelectedFieldData(this.fields);

      if (this.fields[0].data.content.length === 1) {
        this.addAnalysisForm.get(this.fields[0].key).setValue(this.fields[0].data.content[0].key);
        this.selectField(this.fields[0].data.content[0].key, this.fields[0]);
      }

    }

  }

  cancelForm(): void {
    this.customEventService.announceEvent(ActionTypesEnum.CLOSE_MODAL);
  }

  submitForm(): void {
    if (this.addAnalysisForm.get('deploymentAnalytics')?.value === false) {
      Object.keys(this.addAnalysisForm.controls).forEach(field => {
       if (field.startsWith('deploymentAnalytics')) {
         this.addAnalysisForm.removeControl(field);
       }
      });
    }

    if (this.addAnalysisForm.get('deploymentAnalytics')?.value === true && this.addAnalysisForm.get('deploymentAnalyticsReleaseStrategy')?.value==='GITFLOW_RELEASE') {
      const selectDeploymentAnalyticsReleasePattern=this.selectedFields.get("deploymentAnalyticsReleasePattern");
      if(!selectDeploymentAnalyticsReleasePattern){
        return;
      }
    }

    validateFormGroup(this.addAnalysisForm);

    if (this.addAnalysisForm.invalid) {
      return;
    }

    let requestData: any = {
      formData: this.addAnalysisForm.value,
      scoreCards: this.scoreCards
    };

    requestData.formData = requestObjectMapper(requestData.formData, this.fields);

    if (this.isExclusion) {
      requestData = {
        formData: this.addAnalysisForm.value,
        scoreCards: this.scoreCards,
        exclusionPatterns: this.exclusion,
        deploymentAnalyticsPatterns: this.deploymentAnalyticsPatterns,
        analysisDateRange: this.analysisDateRange,
        selectedAgileBoards:this.selectedAgileBoard
      }
    }

    for (const key of Object.keys(requestData.formData)) {
      if(requestData.formData[key] && !(requestData.formData[key] instanceof Array)
        && key !== 'from' && !requestData.formData[key].key
        || (key === 'pullRequestAnalytics' && requestData.formData[key] || !requestData.formData[key])) {
        requestData.formData[key] = {
          key: requestData.formData[key],
          label: requestData.formData[key]
        };
      }
    }

    if(!(requestData.formData["deploymentAnalyticsIncidentSource"]?.key)) {
     delete requestData.formData["deploymentAnalyticsIncidentSource"];
    }

    this.customEventService.announceEvent(ActionTypesEnum.ADD_ANALYSIS, requestData);
  }

  selectField(value, field): void {
    this.resetInputs = false;
    this.selectedFields.set(field.key, value);
    if (field.key === 'deploymentAnalyticsAddons') {
      this.deploymentAnalyticsFields.forEach(item => {
        this.addAnalysisForm.removeControl(item.key);
      });

      this.fields = this.fields.filter(item => !item.deploymentField);
      this.deploymentAnalyticsFields = [];
      this.gitwiserService.deploymentAnalyticsFields(value,false).subscribe(response => {
        this.deploymentAnalyticsFields = response.fields;
        this.fields = this.fields.concat(response.fields);
        response.fields.forEach(item => {
          const formControl = createFormControl(null, item.validators && item.validators.indexOf('required') > -1? Validators['required'] : null);
          this.addAnalysisForm.addControl(item.key, formControl);
          if (!field.required)
          {
            this.addAnalysisForm.removeControl('deploymentAnalyticsIncidentSource')
          }

          if (field.key==='deploymentAnalyticsInitialCommit'){
            this.addAnalysisForm.removeControl('deploymentAnalyticsInitialCommit')
          }

        })
        this.customEventService.announceEvent(ActionTypesEnum.ANALYSIS_CREATE_FIELD_SELECTED, {field, form: this.addAnalysisForm, value});
      });
    } else if (field.key === 'deploymentAnalyticsIncidentSource') {
      if (this.addAnalysisForm.controls.deploymentAnalyticsIncidentSource?.value !== null){
        this.deploymentAnalyticsApmFields.forEach(item => {
          this.addAnalysisForm.removeControl(item.key);
        });
        this.deploymentAnalyticsApmFields = [];
        this.gitwiserService.deploymentAnalyticsApmFields(value).subscribe(response => {
          this.deploymentAnalyticsApmFields = response.fields;
          this.fields = this.fields.concat(response.fields);
          response.fields.forEach(item => {
            const formControl = createFormControl(null, item.validators && item.validators.indexOf('required') > -1? Validators['required'] : null);
            this.addAnalysisForm.addControl(item.key, formControl);
          })
          this.customEventService.announceEvent(ActionTypesEnum.ANALYSIS_CREATE_FIELD_SELECTED, {field, form: this.addAnalysisForm, value});
        });
      }else {
        this.fields.map(item => {
            if (item.name === 'Applications') {
              this.fields.splice(-2);
              this.addAnalysisForm.get("deploymentAnalyticsIncidentSource").clearValidators();
              this.addAnalysisForm.removeControl("deploymentAnalyticsIncidentSourceDatasource");
              this.addAnalysisForm.removeControl("deploymentAnalyticsIncidentApplication");
            }
          }
        )
      }
    } else {
      this.customEventService.announceEvent(ActionTypesEnum.ANALYSIS_CREATE_FIELD_SELECTED, {field, form: this.addAnalysisForm, value});
    }
  }

  createFromArray(fields) {
    const array = new FormArray([]);

    fields.forEach(field => {
      const formValue = null;
      const item = createFormControl(formValue, field.validators && field.validators.indexOf('required') > -1? Validators['required'] : null);
      array.push(item);
    });
    return array;
  }

  checkboxChange(value, field) {
    if (field.key === 'deploymentAnalytics' && !value) {
      this.deploymentAnalyticsFields.forEach(item => {
        this.addAnalysisForm.removeControl(item.key);
      });
      this.deploymentAnalyticsFields = [];
      this.fields = this.fields.filter(item => !item.deploymentField);
    }
  }

  searchSelect(value: string, field: any) {
    this.resetInputs = false;
    const foundValue=  value.length > 0 ?
      field.data.contentAll.filter(item => item.label.toLocaleLowerCase().includes(value.toLocaleLowerCase())).slice(0, 500)
      : field.data.contentAll ? field.data.contentAll.slice(0, 500) : field.data.content;
    if(foundValue && foundValue.length>0){
      field.data.content=foundValue
      return;
    }

    field.loading = false;
    if (field?.widgetFieldListener?.eventType !== 'KEYUP') {
      return;
    }
    if (value.length < 3) {  field.data.content = field.data.defaultContent; return; }

    this.customEventService.announceEvent(ActionTypesEnum.ANALYSIS_CREATE_FIELD_KEYUP_ACTION, {field, form: this.addAnalysisForm, value});
  }

  resetForm(): void {
    this.addAnalysisForm.reset();
    this.resetInputs = true;
  }


  loadAgileBoards(){
    this.agileSpaceService.getAnalysisAgileSpaceAnalysis().pipe(untilDestroyed(this))
    .subscribe(response => {
      this.agilespaceBoard=response

    },error => {
      console.log('error while reanalyse deployment analytics analyse', error);
    });

  }
}
